import { useRef, useState } from "react";
import { CalendarClock, CheckCircle2 } from "lucide-react";
import { useLanding } from "@/context/LandingContext";
import { publicApi } from "@/lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function NotifyForm({ variant = "card", sfx = "" }) {
  const { t, lang } = useLanding();
  const c = t.closed.notify;
  const [value, setValue] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const onSubmit = async (event) => {
    event.preventDefault();
    if (status === "sending" || status === "done") return;
    const email = value.trim();
    if (!EMAIL_RE.test(email)) {
      setError(c.invalid);
      inputRef.current?.focus();
      return;
    }
    setError("");
    setStatus("sending");
    try {
      await publicApi.post("/notify", { email, page_language: lang });
      setStatus("done");
      setValue("");
    } catch {
      setStatus("error");
      setError(c.error);
    }
  };

  if (status === "done") {
    return (
      <p className="notify-success" role="status" aria-live="polite" data-testid={`notify-success${sfx}`}>
        <CheckCircle2 size={18} aria-hidden="true" /> {c.success}
      </p>
    );
  }

  return (
    <form className={`notify-form notify-form-${variant}`} onSubmit={onSubmit} noValidate data-testid={`notify-form${sfx}`}>
      <label className="sr-only" htmlFor={`notifyEmail${sfx}`}>{c.label}</label>
      <input
        ref={inputRef}
        id={`notifyEmail${sfx}`}
        name="notify_email"
        type="email"
        autoComplete="email"
        placeholder={c.placeholder}
        value={value}
        onChange={(e) => { setValue(e.target.value); setError(""); }}
        data-testid={`notify-email-input${sfx}`}
      />
      <button className="btn btn-gold" type="submit" disabled={status === "sending"} data-testid={`notify-submit${sfx}`}>
        {c.button} <span className="arrow">→</span>
      </button>
      {error && <span className="notify-error" role="alert" data-testid={`notify-error${sfx}`}>{error}</span>}
    </form>
  );
}

export function ClosedNotice({ sfx = "" }) {
  const { t } = useLanding();
  const c = t.closed;
  return (
    <div className="closed-notice" data-testid={`closed-notice${sfx}`}>
      <span className="closed-badge" data-testid={`closed-badge${sfx}`}>
        <CalendarClock size={15} aria-hidden="true" /> {c.badge}
      </span>
      <h3 data-testid={`closed-title${sfx}`}>{c.title}</h3>
      <p className="closed-message" data-testid={`closed-message${sfx}`}>{c.message}</p>
      <NotifyForm variant="card" sfx={sfx} />
    </div>
  );
}
